<script setup lang="ts">
const { t } = useI18n()

useHead({
  titleTemplate: () => t("seo.layouts./services.title") || t("services.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./services.description") || t("seo.description") },
    { property: "og:title", content: () => t("seo.layouts./services.title") || t("services.title") },
    { property: "og:description", content: () => t("seo.layouts./services.description") || t("seo.description") },
    { property: "og:image", content: "https://smsbat.com/images/service-type.png" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})
</script>

<template>
  <div>
    <div class="page-title">{{ $t('services.title') }}</div>

    <div class="page-section small-section">
      <div class="container">
        <div class="section-content">
          <div class="text-content">
            {{ $t('services.about[0]') }}
            <span class="highlight-blue">{{ $t('services.about[1]') }}</span>
            {{ $t('services.about[2]') }}
            <span class="highlight-blue">{{ $t('services.about[3]') }}</span>
            {{ $t('services.about[4]') }}
            <span class="highlight-blue">{{ $t('services.about[5]') }}</span>
            {{ $t('services.about[6]') }}
            <span class="highlight-blue">{{ $t('services.about[7]') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section service-types">
      <div class="container">
        <div class="mail-bg-img"></div>
        <div class="phone-bg-img"></div>
        <div class="types-content">
          <div class="types-img">
            <NuxtImg src="/images/service-type.png" width="100" height="100" format="webp" loading="lazy" alt="Service Request" />
          </div>
          <div class="service-list">
            <div class="service-item">{{ $t('services.types[0]') }}</div>
            <div class="service-item">{{ $t('services.types[1]') }}</div>
            <div class="service-item">{{ $t('services.types[2]') }}</div>
            <div class="service-item">{{ $t('services.types[3]') }}</div>
          </div>
          <div class="service-list">
            <div class="service-item">{{ $t('services.types[4]') }}</div>
            <div class="service-item">{{ $t('services.types[5]') }}</div>
            <div class="service-item">{{ $t('services.types[6]') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section service-advantages">
      <div class="container">
        <div class="section-content">
          <div class="advantages-title">
            {{ $t('services.advantages.title') }}
          </div>
          <div class="advantages-list">
            <div class="advantage-item">
              <div class="item-count">01</div>
              <div class="item-title break-all md:break-normal">{{ $t('services.advantages.elems[0].title') }}:</div>
              <div class="item-text break-all md:break-normal">
                {{ $t('services.advantages.elems[0].text') }}
              </div>
            </div>
            <div class="advantage-item">
              <div class="item-count">02</div>
              <div class="item-title break-all md:break-normal">{{ $t('services.advantages.elems[1].title') }}:</div>
              <div class="item-text break-all md:break-normal">
                {{ $t('services.advantages.elems[1].text') }}
              </div>
            </div>
            <div class="advantage-item">
              <div class="item-count">03</div>
              <div class="item-title break-all md:break-normal">{{ $t('services.advantages.elems[2].title') }}:</div>
              <div class="item-text break-all md:break-normal">
                {{ $t('services.advantages.elems[2].text') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section">
      <div class="container">
        <div class="section-content">
          <div class="service-hint">
            <p>
              {{ $t('services.hint[0]') }}
              <span class="highlight-blue">{{ $t('services.hint[1]') }}</span>
              {{ $t('services.hint[2]') }}
            </p>
            <p>
              <span class="highlight-blue">{{ $t('services.hint[3]') }}</span>
              {{ $t('services.hint[4]') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <PriceSection />

    <div class="page-section small-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('services.hosting.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content">
            <p>
              {{ $t('services.hosting.text[0]') }}
              <span class="highlight-blue">{{ $t('services.hosting.text[1]') }}</span>
              {{ $t('services.hosting.text[2]') }}
            </p>
            <p>
              {{ $t('services.hosting.text[3]') }}
              <span class="highlight-blue">{{ $t('services.hosting.text[4]') }}</span>
              {{ $t('services.hosting.text[5]') }}
              <span class="highlight-blue">{{ $t('services.hosting.text[6]') }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section service-hosting">
      <div class="container">
        <div class="hosting-content">
          <div class="hosting-img">
            <NuxtImg src="/images/service-hosting.png" width="100" height="100" format="webp" loading="lazy" alt="Server Hosting" />
          </div>
          <div class="service-list">
            <div class="service-item">{{ $t('services.hosting.elems[0]') }}</div>
            <div class="service-item">{{ $t('services.hosting.elems[1]') }}</div>
            <div class="service-item">{{ $t('services.hosting.elems[2]') }}</div>
          </div>
          <div class="service-list">
            <div class="service-item">{{ $t('services.hosting.elems[3]') }}</div>
            <div class="service-item">{{ $t('services.hosting.elems[4]') }}</div>
          </div>
        </div>
        <div class="service-price">
          <div class="price-label">{{ $t('services.hosting.price[0]') }}</div>
          <div class="price-list">
            <div class="price-item">
              <div class="price-item-label">{{ $t('services.hosting.price[1]') }}</div>
              <div class="price-item-value">{{ $t('services.hosting.price[2]') }}</div>
            </div>
            <div class="price-item">
              <div class="price-item-label">{{ $t('services.hosting.price[3]') }}</div>
              <div class="price-item-value">{{ $t('services.hosting.price[4]') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section small-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('services.domen.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content">
            <p>
              {{ $t('services.domen.text[0]') }}
              <span class="highlight-blue">{{ $t('services.domen.text[1]') }}</span>
              {{ $t('services.domen.text[2]') }}
            </p>
            <p>
              <span class="highlight-blue">{{ $t('services.domen.text[3]') }}</span>
              {{ $t('services.domen.text[4]') }}
            </p>
            <p>
              <span class="highlight-blue">{{ $t('services.domen.text[5]') }}</span>{{ $t('services.domen.text[6]') }}
              <span class="highlight-blue">{{ $t('services.domen.text[7]') }}</span>{{ $t('services.domen.text[8]') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section service-domain">
      <div class="container">
        <div class="domain-content">
          <div class="domain-img">
            <NuxtImg src="/images/service-domain.png" width="100" height="100" format="webp" loading="lazy" alt="Domain Registration" />
          </div>
          <div class="service-price">
            <div class="price-list">
              <div class="price-item">
                <div class="price-item-label">{{ $t('services.domen.price[0]') }}</div>
                <div class="price-item-value">{{ $t('services.domen.price[1]') }}</div>
              </div>
            </div>
            <div class="price-label">{{ $t('services.domen.price[2]') }}</div>
          </div>
          <div class="service-list">
            <div class="service-item">{{ $t('services.domen.price[3]') }}</div>
            <div class="service-item">{{ $t('services.domen.price[4]') }}</div>
            <div class="service-item">{{ $t('services.domen.price[5]') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section small-section">
      <div class="container">
        <div class="section-content">
          <div class="text-content">
            <p>
              {{ $t('services.domen.hint[0]') }}
              <span class="highlight-blue">{{ $t('services.domen.hint[1]') }}</span>
              {{ $t('services.domen.hint[2]') }}
            </p>
            <p>
              {{ $t('services.domen.hint[3]') }}
              <span class="highlight-blue">{{ $t('services.domen.hint[4]') }}</span>
              {{ $t('services.domen.hint[5]') }}
            </p>
            <p>
              {{ $t('services.domen.hint[6]') }}
              <span class="highlight-blue">{{ $t('services.domen.hint[7]') }}</span>
              {{ $t('services.domen.hint[8]') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="page-section small-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('services.flashcall.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content">
            <p>
              {{ $t('services.flashcall.text[0]') }}
              <span class="highlight-blue">{{ $t('services.flashcall.text[1]') }}</span>
            </p>
            <p>
              {{ $t('services.flashcall.text[2]') }}
              <span class="highlight-blue">{{ $t('services.flashcall.text[3]') }}</span>
              {{ $t('services.flashcall.text[4]') }}
            </p>
            <p>
              {{ $t('services.flashcall.text[5]') }}
              <span class="highlight-blue">{{ $t('services.flashcall.text[6]') }}</span>{{
                $t('services.flashcall.text[7]') }}
              <span class="highlight-blue">{{ $t('services.flashcall.text[8]') }}</span>{{
                $t('services.flashcall.text[9]') }}
            </p>
            <p>
              {{ $t('services.flashcall.text[10]') }}
              <span class="highlight-blue">{{ $t('services.flashcall.text[11]') }}</span>{{
                $t('services.flashcall.text[12]') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
